/**
 * Composable for managing GSAP animation cleanup
 * Automatically cleans up registered animations on component unmount
 * to prevent memory leaks
 */
export function useAnimationCleanup() {
  const scrollTriggers = ref([]);
  const timelines = ref([]);
  const delayedCalls = ref([]);
  const splitTexts = ref([]);

  /**
   * Clean up all registered animations
   */
  const cleanup = () => {
    // Kill all ScrollTrigger instances
    scrollTriggers.value.forEach(st => {
      if (st && typeof st.kill === 'function') {
        st.kill();
      }
    });

    // Kill all GSAP timelines
    timelines.value.forEach(tl => {
      if (tl && typeof tl.kill === 'function') {
        tl.kill();
      }
    });

    // Kill all delayed calls
    delayedCalls.value.forEach(dc => {
      if (dc && typeof dc.kill === 'function') {
        dc.kill();
      }
    });

    // Revert all SplitText instances
    splitTexts.value.forEach(st => {
      if (st && typeof st.revert === 'function') {
        st.revert();
      }
    });

    // Clear all arrays
    scrollTriggers.value = [];
    timelines.value = [];
    delayedCalls.value = [];
    splitTexts.value = [];
  };

  // Automatically cleanup on component unmount
  onUnmounted(cleanup);

  return {
    /**
     * Register a ScrollTrigger instance for automatic cleanup
     * @param {ScrollTrigger} st - ScrollTrigger instance
     */
    registerScrollTrigger: st => {
      if (st) scrollTriggers.value.push(st);
      return st;
    },

    /**
     * Register a GSAP timeline for automatic cleanup
     * @param {gsap.core.Timeline} tl - GSAP timeline
     */
    registerTimeline: tl => {
      if (tl) timelines.value.push(tl);
      return tl;
    },

    /**
     * Register a gsap.delayedCall for automatic cleanup
     * @param {gsap.core.Tween} dc - Delayed call instance
     */
    registerDelayedCall: dc => {
      if (dc) delayedCalls.value.push(dc);
      return dc;
    },

    /**
     * Register a SplitText instance for automatic cleanup
     * @param {SplitText} st - SplitText instance
     */
    registerSplitText: st => {
      if (st) splitTexts.value.push(st);
      return st;
    },

    /**
     * Manually trigger cleanup (useful for early cleanup)
     */
    cleanup
  };
}
